// © 2026 AttainIP Engineering

/**
 * App-root banner for unhandled query/mutation errors surfaced via the
 * QueryClient onError sink (RFC-0202 §"Session-Loss UX" §2).
 *
 * Session-loss (SESSION_EXPIRED) is NOT shown here — that path triggers a
 * login redirect instead. This banner covers everything else that would
 * otherwise fail silently (ss-api restart, network blip during a poll), so
 * the analysis progress page no longer just freezes at its last-known state.
 *
 * A11y: role="alert" + aria-live="assertive" so the message is announced.
 * The dismiss button has an accessible label.
 *
 * Stacking: the store holds a single message (replace semantics); a newer
 * error overwrites the visible one. No queue.
 */

import { AlertCircle, X } from 'lucide-react';
import { useErrorBannerStore } from '../store/errorBanner';

export function ErrorBanner() {
  const message = useErrorBannerStore((s) => s.message);
  const clear = useErrorBannerStore((s) => s.clear);

  if (!message) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="fixed top-0 inset-x-0 z-[100] flex justify-center px-ds-4 pt-ds-3 pointer-events-none"
    >
      <div className="pointer-events-auto flex items-start gap-ds-2 w-full max-w-[520px] bg-[#fef2f2] border border-[#fecaca] rounded-ds-sm p-3 shadow-[0_4px_24px_rgba(0,0,0,0.12)]">
        <AlertCircle className="h-4 w-4 text-ds-danger shrink-0 mt-0.5" />
        <p className="flex-1 text-sm text-ds-text">{message}</p>
        <button
          type="button"
          onClick={clear}
          aria-label="Dismiss"
          className="shrink-0 text-ds-text-secondary hover:text-ds-text"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
